import React, { useCallback, useMemo, FC } from 'react';
import './SatisfactionDataFullView.scss';
import { SatisfactionLevelSelector } from './SatisfactionLevelSelector';
import { SatisfactionCalendar } from './SatisfactionCalendar';
import { CircularProgress, Stack } from '@mui/material';
import {
  selectedDateAtom,
  useSatisfactionEmas,
  useUpdateSatisfactionEma,
} from '../../../../../data-access';
import { useAuthContext } from '../../../../../data-access';
import { useRecoilValue } from 'recoil';
import dayjs from 'dayjs';

import { debounce } from 'lodash';
import { trackEventMixpanel } from '../../../../../utils';
import { satisfactionOptions } from '../../../constants';
import { EMASatisfactionType } from '@demind-inc/core';
import SubjectiveFeelings from './SubjectiveFeelings';

const SatisfactionDataFullView: FC = () => {
  const { user } = useAuthContext();
  const selectedDate = useRecoilValue(selectedDateAtom);
  const { satisfactionEmas, isLoading } = useSatisfactionEmas({
    userId: user?.userId,
    startDate: dayjs(selectedDate).startOf('month').format('YYYY-MM-DD'),
    endDate: dayjs(selectedDate).endOf('month').format('YYYY-MM-DD'),
  });
  const { mutate: updateSatisfactionEma } = useUpdateSatisfactionEma();

  const currentDate = useMemo(() => dayjs(selectedDate).format('YYYY-MM-DD'), [selectedDate]);

  const getSatisfactionForDate = useCallback(
    (date) => {
      return satisfactionEmas.find((day) => day.date === date)?.satisfaction?.value || null;
    },
    [satisfactionEmas]
  );

  const selectedSatisfaction = getSatisfactionForDate(currentDate);

  const debouncedUpdateSatisfactionEma = useMemo(
    () =>
      debounce((date: string, value: EMASatisfactionType) => {
        if (!user?.userId) return;
        updateSatisfactionEma({
          userId: user?.userId,
          date,
          satisfaction: value,
        });
        trackEventMixpanel('satisfaction_answer', {
          satisfaction: value,
          from: 'satisfaction_full_view',
        });
      }, 2000),
    [user?.userId, updateSatisfactionEma]
  );

  const handleSatisfactionChange = useCallback(
    (date: string, value: EMASatisfactionType) => {
      debouncedUpdateSatisfactionEma(date, value);
    },
    [debouncedUpdateSatisfactionEma]
  );

  return (
    <Stack gap={6} justifyContent="space-between" direction="row" className="satisfaction-data-full-view">
      <div className="satisfaction-data-full-view__level-selector-container">
        {isLoading ? (
          <div className="satisfaction-data-full-view__loading">
            <CircularProgress />
          </div>
        ) : (
          <>
            <SatisfactionLevelSelector
              selectedSatisfaction={selectedSatisfaction}
              onSatisfactionChange={handleSatisfactionChange}
              satisfactionOptions={satisfactionOptions}
              currentDate={currentDate}
            />
            <SatisfactionCalendar
              data={satisfactionEmas}
              selectedDate={selectedDate}
              onSatisfactionChange={handleSatisfactionChange}
            />
          </>
        )}
      </div>
      <SubjectiveFeelings />
    </Stack>
  );
};

export default SatisfactionDataFullView;
